import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Search, X, Upload, Trash2, GripVertical, Loader2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  DndContext, closestCenter, SortableContext, arrayMove,
  rectSortingStrategy, SortableItem, useDndSensors,
} from "./Sortable";
import {
  apiSearchProducts, apiGetProductFull, apiCreateReview, apiUpdateReview,
  apiUploadImage, apiDeleteAsset, apiMeta, abbreviateName, errMsg, cldThumb, GRADE_LABELS,
} from "../adminApi";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

const variantText = (product, v) => {
  if (!product || !v) return "";
  const c = (product.colors || []).find((x) => x.id === v.color_id);
  const parts = [];
  if (c) parts.push(c.name);
  if (v.memory) parts.push(`${v.memory}GB`);
  if (v.grade) parts.push(GRADE_LABELS[v.grade] || v.grade);
  return parts.join(" · ");
};

export function ReviewDialog({ open, onOpenChange, review, presetProduct, onSaved }) {
  const sensors = useDndSensors();
  const [cloudinaryOk, setCloudinaryOk] = useState(false);
  const [form, setForm] = useState({});
  const [product, setProduct] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { apiMeta().then((m) => setCloudinaryOk(m.cloudinary_configured)).catch(() => {}); }, []);

  useEffect(() => {
    if (!open) return;
    const base = review ? { ...review } : {
      stars: 5, status: "PUBLISHED", verified: false, featured: false, photos: [],
      display_name: "", city: "", title: "", text: "", shop_reply: "", order_ref: "",
      product_id: presetProduct?.id || "", variant_id: null,
    };
    setForm(base);
    const pid = base.product_id;
    if (pid) apiGetProductFull(pid).then(setProduct).catch(() => setProduct(null));
    else setProduct(presetProduct || null);
  }, [open, review, presetProduct]);

  useEffect(() => {
    if (!q) { setResults([]); return; }
    const t = setTimeout(() => apiSearchProducts(q).then(setResults).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const up = (patch) => setForm((f) => ({ ...f, ...patch }));

  const pickProduct = async (p) => {
    up({ product_id: p.id, variant_id: null });
    setQ(""); setResults([]);
    try { setProduct(await apiGetProductFull(p.id)); } catch { setProduct(null); }
  };

  const handleFiles = async (files) => {
    if (!cloudinaryOk) { toast.error("Cloudinary non configurato: impossibile caricare foto."); return; }
    const current = form.photos || [];
    if (current.length >= 6) { toast.error("Massimo 6 foto."); return; }
    setUploading(true);
    const added = [];
    for (const file of Array.from(files).slice(0, 6 - current.length)) {
      try {
        const res = await apiUploadImage(file, "rinova/reviews");
        added.push({ id: uid(), public_id: res.public_id, url: res.url });
      } catch (e) { toast.error(errMsg(e)); }
    }
    if (added.length) up({ photos: [...current, ...added] });
    setUploading(false);
  };
  const deletePhoto = async (photo) => {
    try { if (photo.public_id) await apiDeleteAsset(photo.public_id); } catch { /* ignore */ }
    up({ photos: (form.photos || []).filter((p) => p.id !== photo.id) });
  };
  const onPhotoDrag = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ph = form.photos || [];
    up({ photos: arrayMove(ph, ph.findIndex((p) => p.id === active.id), ph.findIndex((p) => p.id === over.id)) });
  };

  const save = async () => {
    if (!form.product_id) { toast.error("Scegli un prodotto."); return; }
    if (!(form.display_name || "").trim()) { toast.error("Inserisci il nome visualizzato."); return; }
    setSaving(true);
    try {
      if (review) await apiUpdateReview(review.id, form);
      else await apiCreateReview(form);
      toast.success("Recensione salvata");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const photos = form.photos || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>{review ? "Modifica recensione" : "Aggiungi recensione"}</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-4">
          {/* Prodotto */}
          {!presetProduct && (
            <div className="flex flex-col gap-1.5">
              <Label>Prodotto</Label>
              {form.product_id && product ? (
                <div className="flex items-center justify-between rounded-lg border border-line bg-surface3 px-3 py-2 text-sm text-chalk">
                  {product.name}
                  <button onClick={() => { up({ product_id: "", variant_id: null }); setProduct(null); }}><X className="h-4 w-4 text-steel" /></button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tertiary" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca prodotto" className="pl-9" data-testid="review-product-search" />
                  {results.length > 0 && (
                    <div className="absolute z-10 mt-1 max-h-40 w-full overflow-y-auto rounded-lg border border-line bg-surface3">
                      {results.map((r) => <button key={r.id} className="block w-full px-3 py-2 text-left text-sm text-chalk hover:bg-surface" onClick={() => pickProduct(r)}>{r.name}</button>)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Variante */}
          {product && (product.variants || []).length > 0 && (
            <div className="flex flex-col gap-1.5">
              <Label>Variante acquistata (facolt.)</Label>
              <Select value={form.variant_id || "none"} onValueChange={(v) => up({ variant_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="review-variant-select"><SelectValue placeholder="Nessuna" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna</SelectItem>
                  {product.variants.map((v) => <SelectItem key={v.id} value={v.id}>{variantText(product, v)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Stelle */}
          <div className="flex flex-col gap-1.5">
            <Label>Valutazione</Label>
            <div className="flex gap-1" data-testid="review-stars-picker">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => up({ stars: n })} aria-label={`${n} stelle`}>
                  <Star className={`h-7 w-7 ${n <= (form.stars || 0) ? "fill-lime text-lime" : "text-tertiary"}`} />
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Nome visualizzato</Label>
              <div className="flex gap-2">
                <Input value={form.display_name || ""} onChange={(e) => up({ display_name: e.target.value })} data-testid="review-name-input" />
                <Button variant="secondary" size="icon" title="Abbrevia (es. Marco R.)" onClick={() => up({ display_name: abbreviateName(form.display_name) })}><Wand2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="flex flex-col gap-1.5"><Label>Città (facolt.)</Label><Input value={form.city || ""} onChange={(e) => up({ city: e.target.value })} /></div>
          </div>

          <div className="flex flex-col gap-1.5"><Label>Titolo</Label><Input value={form.title || ""} onChange={(e) => up({ title: e.target.value })} /></div>
          <div className="flex flex-col gap-1.5"><Label>Testo</Label><Textarea rows={4} value={form.text || ""} onChange={(e) => up({ text: e.target.value })} /></div>

          {/* Foto */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Label>Foto ({photos.length}/6)</Label>
              <label>
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid="review-photo-upload" disabled={!cloudinaryOk} />
                <Button asChild variant="secondary" size="sm" disabled={!cloudinaryOk || photos.length >= 6}><span className="cursor-pointer">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Carica</span></Button>
              </label>
            </div>
            {photos.length > 0 && (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onPhotoDrag}>
                <SortableContext items={photos.map((p) => p.id)} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-4 gap-2">
                    {photos.map((ph) => (
                      <SortableItem key={ph.id} id={ph.id}>
                        {({ setNodeRef, style, attributes, listeners }) => (
                          <div ref={setNodeRef} style={style} {...attributes} className="group relative overflow-hidden rounded-lg border border-line">
                            <button className="absolute left-1 top-1 z-10 cursor-grab rounded bg-ink/60 p-0.5 text-chalk" {...listeners}><GripVertical className="h-3 w-3" /></button>
                            <img src={cldThumb(ph.url, "c_pad,w_200,h_200,b_transparent,f_auto,q_auto")} alt="" className="aspect-square w-full object-cover" />
                            <button className="absolute right-1 top-1 z-10 rounded bg-ink/60 p-0.5 text-destructive" onClick={() => deletePhoto(ph)}><Trash2 className="h-3 w-3" /></button>
                          </div>
                        )}
                      </SortableItem>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5"><Label>Riferimento ordine (solo pannello)</Label><Input value={form.order_ref || ""} onChange={(e) => up({ order_ref: e.target.value })} /></div>
            <div className="flex flex-col gap-1.5"><Label>Stato</Label>
              <Select value={form.status} onValueChange={(v) => up({ status: v })}>
                <SelectTrigger data-testid="review-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="PUBLISHED">Pubblicata</SelectItem><SelectItem value="HIDDEN">Nascosta</SelectItem></SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between"><Label>Acquisto verificato</Label><Switch checked={!!form.verified} onCheckedChange={(v) => up({ verified: v })} data-testid="review-verified-switch" /></div>
          <div className="flex items-center justify-between"><Label>In evidenza in homepage</Label><Switch checked={!!form.featured} onCheckedChange={(v) => up({ featured: v })} data-testid="review-featured-switch" /></div>

          <div className="flex flex-col gap-1.5"><Label>Risposta del negozio (facolt.)</Label><Textarea rows={2} value={form.shop_reply || ""} onChange={(e) => up({ shop_reply: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button variant="lime" onClick={save} disabled={saving} data-testid="review-save-button">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
